import { ConversationSession, CONVERSATION_SCHEMA_VERSION } from './types';

/**
 * Persistence boundary for conversation history.
 *
 * All methods are async so a backend-backed implementation (e.g. `HttpConversationStore` talking to
 * Kois.Api) can replace `LocalStorageConversationStore` without touching the engine or the widget.
 */
export interface ConversationStore {
  loadCurrent(userKey: string): Promise<ConversationSession | null>;
  saveCurrent(session: ConversationSession): Promise<void>;
  /** Moves a finished session into the archive list for its user. */
  archive(session: ConversationSession): Promise<void>;
  listArchived(userKey: string): Promise<ConversationSession[]>;
  clearCurrent(userKey: string): Promise<void>;
}

export const CURRENT_SESSION_KEY_PREFIX = 'askAi.conversation.v1:';
export const ARCHIVE_KEY_PREFIX = 'askAi.conversation.archive.v1:';
export const MAX_ARCHIVED_SESSIONS = 5;

/** Keys written by the previous SmartClinic-backed widget. Removed once on first load. */
export const LEGACY_KEYS = ['aiSessionId', 'aiSessionLastActivity', 'askAiLastFeedbackMessageId'];

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const defaultStorage = (): StorageLike | null => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
};

const isSession = (value: any): value is ConversationSession =>
  !!value && typeof value === 'object' && value.schemaVersion === CONVERSATION_SCHEMA_VERSION &&
  typeof value.id === 'string' && Array.isArray(value.messages);

export class LocalStorageConversationStore implements ConversationStore {
  private readonly storage: StorageLike | null;
  private readonly maxArchived: number;

  constructor(storage: StorageLike | null = defaultStorage(), maxArchived = MAX_ARCHIVED_SESSIONS) {
    this.storage = storage;
    this.maxArchived = maxArchived;
  }

  private read<T>(key: string): T | null {
    if (!this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  private write(key: string, value: unknown): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn('[askAi] could not persist conversation', error);
    }
  }

  async loadCurrent(userKey: string): Promise<ConversationSession | null> {
    const session = this.read<ConversationSession>(CURRENT_SESSION_KEY_PREFIX + userKey);
    return isSession(session) && session.userKey === userKey ? session : null;
  }

  async saveCurrent(session: ConversationSession): Promise<void> {
    this.write(CURRENT_SESSION_KEY_PREFIX + session.userKey, session);
  }

  async archive(session: ConversationSession): Promise<void> {
    const key = ARCHIVE_KEY_PREFIX + session.userKey;
    const existing = (this.read<ConversationSession[]>(key) || []).filter(isSession);
    const next = [session, ...existing.filter((s) => s.id !== session.id)].slice(0, this.maxArchived);
    this.write(key, next);
  }

  async listArchived(userKey: string): Promise<ConversationSession[]> {
    return (this.read<ConversationSession[]>(ARCHIVE_KEY_PREFIX + userKey) || []).filter(isSession);
  }

  async clearCurrent(userKey: string): Promise<void> {
    if (!this.storage) return;
    try {
      this.storage.removeItem(CURRENT_SESSION_KEY_PREFIX + userKey);
    } catch {}
  }
}

/** Deletes keys left behind by the SmartClinic-backed widget. Safe to call repeatedly. */
export function removeLegacyKeys(storage: StorageLike | null = defaultStorage()): void {
  if (!storage) return;
  LEGACY_KEYS.forEach((key) => {
    try { storage.removeItem(key); } catch {}
  });
}
